import UserFinderClass from "../../../user/application/user-finder";
import MultilingualServiceClass from "../../../../libs/application/multilingual-service";
import { CustomDate } from "../../domain/classes/custom-date";

export type UnitSystem = "si" | "us" | "uk";

export class DataFormatterUtilClass {
	public unit_system: UnitSystem;
	private user_finder: UserFinderClass;
	private multilingual_service: MultilingualServiceClass;

	constructor(
		user_finder?: UserFinderClass,
		multilingual_service?: MultilingualServiceClass
	) {
		this.user_finder = user_finder || new UserFinderClass();
		this.multilingual_service =
			multilingual_service || new MultilingualServiceClass();

		const current_user = this.user_finder.get_stored_user();
		this.unit_system = (current_user?.unit_system as UnitSystem) || "si";
	}

	is_number(value: any): value is number {
		return typeof value === "number" && !Number.isNaN(value);
	}

	is_string(value: any): value is string {
		return typeof value === "string";
	}

	is_function(value: any): value is Function {
		return typeof value === "function";
	}

	is_date(value: any): boolean {
		if (value === null || value === undefined) return false;
		const date_wrapper = new Date(value);
		return !isNaN(date_wrapper.getTime());
	}

	get_year_month_day_from_isodate(value: string): string {
		if (!value) return "";
		return value.split("T")[0];
	}

	get_year_month_from_isodate(value: string): string {
		if (!value) return "";
		return value.substring(0, 7);
	}

	/**
	 * Formats a number based on the current unit system, decimal preference, and thousand separator option.
	 */
	format_number(
		number_to_format: number | string | null,
		decimals: number | null = null,
		hide_thousands_separator: boolean = false
	): string | null {
		if (number_to_format === null || number_to_format === undefined)
			return null;

		let parsed_number =
			typeof number_to_format === "string"
				? Number(number_to_format)
				: number_to_format;

		if (Number.isNaN(parsed_number)) return null;

		if (decimals !== null) {
			parsed_number = this.round_number(parsed_number, decimals);
		}

		return this._format_number_to_unit_system(
			parsed_number,
			hide_thousands_separator
		);
	}

	private _format_number_to_unit_system(
		number_to_format: number,
		hide_thousands_separator: boolean = false
	): string {
		// de-DE uses dot for thousands and comma for decimals (consistent with SI/Europe)
		const locale = this.unit_system === "si" ? "de-DE" : "en-US";
		const number_as_string = number_to_format.toString();
		const decimal_position = number_as_string.indexOf(".");
		const number_of_decimals =
			decimal_position === -1
				? 0
				: number_as_string.length - (decimal_position + 1);

		return Intl.NumberFormat(locale, {
			style: "decimal",
			minimumFractionDigits: number_of_decimals,
			useGrouping: !hide_thousands_separator
		}).format(number_to_format);
	}

	round_number(number_to_round: number, decimals: number): number {
		if (decimals === 0) {
			return Math.round(number_to_round);
		}
		const factor = Math.pow(10, decimals);
		return Math.round((number_to_round + Number.EPSILON) * factor) / factor;
	}

	/**
	 * Parses a localized string number back to a JS number.
	 */
	parse_to_number(string_number: string): number | null {
		const parse_number = (value: string, locale: string) => {
			const example = Intl.NumberFormat(locale).format(1.1);
			const decimal_separator = example.charAt(1);
			const clean_pattern = new RegExp(`[^-+0-9${decimal_separator}]`, "g");
			const cleaned = value.replace(clean_pattern, "");
			const normalized = cleaned.replace(decimal_separator, ".");

			return parseFloat(normalized);
		};

		const locale = this.unit_system === "si" ? "es-ES" : "en-US";
		const result = parse_number(string_number, locale);

		return isNaN(result) ? null : result;
	}

	// --- START Date Formatting ---
	format_date_to_day(date: Date | string | CustomDate): string {
		const custom_date = new CustomDate(date);
		switch (this.unit_system) {
			case "us":
				return custom_date.format("MM-DD-YYYY");
			case "uk":
				return custom_date.format("YYYY-MM-DD");
			default:
				return custom_date.format("DD-MM-YYYY");
		}
	}

	format_date_to_day_number(date: Date | string | CustomDate): string {
		return new CustomDate(date).format("D");
	}

	format_date_to_day_hour_minute(date: Date | string | CustomDate): string {
		return `${this.format_date_to_day(date)} ${this.format_date_to_hour_minute(date)}`;
	}

	format_date_to_hour_minute(date: Date | string | CustomDate): string {
		return new CustomDate(date).format("HH:mm");
	}

	format_date_to_month(date: Date | string | CustomDate): string {
		const custom_date = new CustomDate(date);
		return this.unit_system === "si"
			? custom_date.format("MM-YYYY")
			: custom_date.format("YYYY-MM");
	}

	format_date_to_month_number(date: Date | string | CustomDate): number {
		return Number(new CustomDate(date).format("M"));
	}

	format_date_to_month_string(date: Date | string | CustomDate): string {
		const month_number = new CustomDate(date).format("M");
		return this.multilingual_service.translate(
			`general.months.${month_number}`
		);
	}

	format_date_to_month_abbreviated_string(
		date: Date | string | CustomDate
	): string {
		return this.format_date_to_month_string(date).substring(0, 3);
	}

	format_date_to_month_day_number(date: Date | string | CustomDate): number {
		return Number(new CustomDate(date).format("D"));
	}

	format_date_to_year_number(date: Date | string | CustomDate): number {
		return Number(new CustomDate(date).format("YYYY"));
	}
	// --- END Date Formatting ---

	get_object_value_from_path(object: any, path: string): any {
		return path
			.split(".")
			.reduce(
				(acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
				object
			);
	}
}

export const DataFormatterUtil = new DataFormatterUtilClass();
